/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {sendGaPageView, trackPlatformAction} from '../../util/analytics';
import {NOTIFICATIONS_ROUTE} from '../../util/constants';
import {onSetCrumbs} from '../../util/events';
import {AppContext} from '../misc/appContext';
import Subscription from '../../models/subscriptionModel';
import {NotificationType, SubscriptionModuleName} from '../../util/enums';
import {createErrorMessage} from '../../util/utils';
import {ResponseData} from '@dvrd/dvr-controls';
import TestMailController, {TestMailRef} from '../email/test/testMailController';
import Notification, {defaultNotificationValues, freeNotificationTypes} from '../../models/notificationModel';
import {sendTestNotification} from '../../util/requestSender';
import NotificationsPage from './notificationsPage';
import {AccessKey, ActivateHandle} from '../../util/interfaces';
import useRight from '../misc/useRight';
import AddNotification from './addNotification/addNotification';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function NotificationsPageController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const [canEdit, canDelete] = useRight(AccessKey.NOTIFICATIONS_EDIT, AccessKey.NOTIFICATIONS_DELETE);
    const [notifications, setNotifications] = useState<Array<Notification>>([]);
    const [loading, setLoading] = useState(true);
    const testMailRef = useRef<TestMailRef>(null);
    const testingNotification = useRef<Notification | null>(null);
    const addRef = useRef<ActivateHandle>(null);
    const moduleActive = useMemo(() => {
        return !!user?.subscription?.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS);
    }, [user]);

    function onActivateModule() {
        if (!account || !user?.subscription) return;
        setLoading(true);
        const subscription = user.subscription;
        subscription.addModule(
            {accountID: account.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
            (subscription: Subscription, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    user.subscription = subscription;
                    context.onChangeContext({user});
                    showSnackbar(
                        t('notifications.list.moduleAddedSnackbar', 'De module is toegevoegd aan het abonnement')
                    );
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'notifications.list.moduleAddErrorMessage',
                                    'Het toevoegen van de module is niet gelukt.'
                                )
                        ),
                        t('notifications.list.moduleAddErrorTitle', 'Toevoegen mislukt')
                    );
            }
        );
    }

    function onClickAdd() {
        if (!canEdit) return;
        // if (!moduleActive) return onPulseModule();
        addRef.current?.activate();
    }

    function onAddNotification(notificationType: NotificationType) {
        if (!account || !canEdit) return;
        if (!moduleActive && !freeNotificationTypes.includes(notificationType)) return onPulseModule();
        setLoading(true);
        const notification = new Notification({
            notification_type: notificationType,
            email_addresses: [account.email],
            account_id: account.id,
            ...defaultNotificationValues[notificationType]
        });
        notification.create((notification: Notification, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) setNotifications(notifications.concat(notification));
            else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'notifications.list.notificationAddErrorMessage',
                                'Het toevoegen van de notificatie is niet gelukt.'
                            )
                    ),
                    t('notifications.list.notificationAddErrorTitle', 'Toevoegen mislukt')
                );
        });
    }

    function onChangeNotification(notification: Notification) {
        if (!canEdit) return;
        const _notifications = notifications.slice();
        for (let ix = 0; ix < _notifications.length; ix++) {
            if (_notifications[ix].id !== notification.id) continue;
            notification.update();
            trackPlatformAction('notifications', 'save_settings');
            _notifications.splice(ix, 1, notification);
            break;
        }
        setNotifications(_notifications);
    }

    function onReplaceNotification(notification: Notification) {
        const _notifications = notifications.slice();
        for (let ix = 0; ix < notifications.length; ix++) {
            if (_notifications[ix].id === notification.id) {
                _notifications.splice(ix, 1, notification);
                break;
            }
        }
        setNotifications(_notifications);
    }

    function onSendTestNotification(notification: Notification) {
        return function () {
            testingNotification.current = notification;
            testMailRef.current?.activate(notification.firstEmailAddress);
        };
    }

    function onSendTestMail(email: string) {
        if (!testingNotification.current) return;
        testMailRef.current?.setLoading(true);
        const notification = testingNotification.current;
        sendTestNotification({
            data: {
                account_id: notification.accountId,
                email_address: email,
                notification_id: notification.id
            },
            callback: (data: ResponseData) => {
                testMailRef.current?.setLoading(false);
                if (data.success) {
                    testMailRef.current?.onClose();
                    showSnackbar(t('notifications.list.notificationSentSnackbar', 'De notificatie is verzonden'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'notifications.list.notificationSendErrorMessage',
                                    'Het verzenden van de notificatie is niet gelukt.'
                                )
                        ),
                        t('notifications.list.notificationSendErrorTitle', 'Verzenden mislukt')
                    );
            }
        });
    }

    function onDeleteNotification(notification: Notification) {
        return function () {
            if (!canDelete) return;
            showDialog(
                t('notifications.list.deleteConfirmMessage', 'Weet je zeker dat je deze notificatie wilt verwijderen?'),
                t('notifications.list.deleteConfirmTitle', 'Notificatie verwijderen'),
                [
                    t('notifications.list.deleteConfirmNo', 'Nee'),
                    {
                        label: t('notifications.list.deleteConfirmYes', 'Ja'),
                        onClick: onConfirmDelete(notification),
                        primary: false
                    }
                ]
            );
        };
    }

    function onConfirmDelete(notification: Notification) {
        return function () {
            setLoading(true);
            notification.delete((_: Notification, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setNotifications((notifications: Array<Notification>) =>
                        notifications.filter((noti: Notification) => noti.id !== notification.id)
                    );
                    showSnackbar(t('notifications.list.notificationDeletedSnackbar', 'De notificatie is verwijderd'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'notifications.list.notificationDeleteErrorMessage',
                                    'Het verwijderen van de notificatie is niet gelukt.'
                                )
                        ),
                        t('notifications.list.notificationDeleteErrorTitle', 'Verwijderen mislukt')
                    );
            });
        };
    }

    function onPulseModule() {
        const button = document.getElementById('btn-activate');
        if (!button) return;
        button.addEventListener('animationend', () => {
            button.classList.remove('pulse');
        });
        button.classList.add('pulse');
    }

    function loadNotifications() {
        setLoading(true);
        if (!account) return setLoading(false);
        Notification.getAll(account.id, (notifications: Array<Notification>, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) setNotifications(notifications);
            else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('notifications.list.loadErrorMessage', 'Er zijn geen notificaties gevonden.')
                    ),
                    t('notifications.list.loadErrorTitle', 'Notificaties niet gevonden')
                );
        });
    }

    useEffect(() => {
        sendGaPageView(NOTIFICATIONS_ROUTE);
        onSetCrumbs([{label: t('notifications.list.breadcrumb', 'Notificaties')}]);
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [account?.id]);

    return (
        <>
            <NotificationsPage
                onActivateModule={onActivateModule}
                onSendTestNotification={onSendTestNotification}
                onClickAdd={onClickAdd}
                onChangeNotification={onChangeNotification}
                onDeleteNotification={onDeleteNotification}
                onReplaceNotification={onReplaceNotification}
                loading={loading}
                notifications={notifications}
                moduleActive={moduleActive}
                canEdit={canEdit}
                canDelete={canDelete}
            />
            <TestMailController onSubmit={onSendTestMail} ref={testMailRef} closeOnSubmit={false} />
            <AddNotification onChooseType={onAddNotification} ref={addRef} moduleActive={moduleActive} />
        </>
    );
}
